import { el } from './dom';
import type { Hotspot } from '../lib/preview/hotspots';
import { hitTestHotspot } from '../lib/preview/hotspots';
import type { RedactionStore } from '../lib/redactionStore';

const truncate = (s: string, max = 80): string =>
  s.length > max ? s.slice(0, max - 1) + '…' : s;

/**
 * Hover-to-reveal layer for the preview canvas. A friend testing the
 * tool pointed out that going through the sidebar to figure out which
 * redaction covers which text is tedious — it's much faster to mouse
 * over the black bar and see what was underneath.
 *
 * This module owns three things:
 *   - the current page's list of hotspots (updated each render),
 *   - a floating tooltip element that follows the cursor when over a
 *     redaction, showing the redacted text + type,
 *   - canvas-level pointer handlers that hit-test and (in non-manual
 *     mode) toggle the entry off when clicked.
 *
 * Manual-mode drag-to-redact is the other consumer of pointerdown on
 * the canvas. We refuse to toggle when modeState.active is true so
 * the two never fight.
 */
export type RedactionHoverHandle = {
  /** Replace the active hotspot list. Call after each canvas render. */
  setHotspots: (hotspots: readonly Hotspot[]) => void;
  /** Detach event listeners + remove the tooltip from the DOM. */
  dispose: () => void;
};

export const installRedactionHover = (
  wrap: HTMLElement,
  canvas: HTMLCanvasElement,
  store: RedactionStore,
  modeState: { active: boolean },
): RedactionHoverHandle => {
  let hotspots: readonly Hotspot[] = [];
  let lastHit: Hotspot | undefined;

  const tooltip = el(`
    <div class="redaction-tooltip" hidden role="tooltip">
      <div class="redaction-tooltip-label"></div>
      <div class="redaction-tooltip-type"></div>
      <div class="redaction-tooltip-hint">click to remove</div>
    </div>
  `);
  const labelEl = tooltip.querySelector<HTMLElement>('.redaction-tooltip-label');
  const typeEl = tooltip.querySelector<HTMLElement>('.redaction-tooltip-type');
  // Anchor the tooltip in the canvas wrap so its positioning is
  // relative to the wrap's coordinate system (no need to compensate
  // for page scroll on every move).
  if (!wrap.style.position) wrap.style.position = 'relative';
  wrap.append(tooltip);

  const setTooltipText = (hit: Hotspot): void => {
    if (labelEl) labelEl.textContent = truncate(hit.label);
    if (typeEl) typeEl.textContent = hit.typeLabel;
  };

  const positionTooltip = (cssX: number, cssY: number, hit: Hotspot): void => {
    // Offset the tooltip below+right of the cursor. Clamp inside the
    // wrap so it doesn't get cut off near the right edge or bottom.
    const wrapRect = wrap.getBoundingClientRect();
    const TT_PAD = 12;
    tooltip.style.left = `${String(cssX + TT_PAD)}px`;
    tooltip.style.top = `${String(cssY + TT_PAD)}px`;
    tooltip.hidden = false;
    // Re-measure after we've made it visible. Flip to the left / above
    // when the tooltip would overflow.
    const ttRect = tooltip.getBoundingClientRect();
    if (cssX + TT_PAD + ttRect.width > wrapRect.width) {
      tooltip.style.left = `${String(cssX - TT_PAD - ttRect.width)}px`;
    }
    if (cssY + TT_PAD + ttRect.height > wrapRect.height) {
      tooltip.style.top = `${String(cssY - TT_PAD - ttRect.height)}px`;
    }
    setTooltipText(hit);
  };

  const hideTooltip = (): void => {
    tooltip.hidden = true;
    lastHit = undefined;
    wrap.classList.remove('is-hover-redaction');
  };

  const onMove = (e: PointerEvent): void => {
    if (modeState.active) {
      hideTooltip();
      return;
    }
    // Hit-test against the CANVAS's coords (hotspots are normalised
    // against the canvas), but listen on the WRAP — the wrap contains
    // the canvas AND the pdfjs text-selection layer that overlays it,
    // and text-layer spans intercept pointermove on the canvas itself.
    // Without listening on the wrap, hover only triggers in slivers
    // where text spans don't cover the canvas.
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xNorm = (e.clientX - rect.left) / rect.width;
    const yNorm = (e.clientY - rect.top) / rect.height;
    const hit = hitTestHotspot(hotspots, xNorm, yNorm);
    if (!hit) {
      hideTooltip();
      return;
    }
    lastHit = hit;
    wrap.classList.add('is-hover-redaction');
    // Position relative to the wrap, not the viewport.
    const wrapRect = wrap.getBoundingClientRect();
    positionTooltip(e.clientX - wrapRect.left, e.clientY - wrapRect.top, hit);
  };

  const onLeave = (): void => {
    hideTooltip();
  };

  const onClick = (e: PointerEvent): void => {
    if (modeState.active) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xNorm = (e.clientX - rect.left) / rect.width;
    const yNorm = (e.clientY - rect.top) / rect.height;
    const hit = hitTestHotspot(hotspots, xNorm, yNorm);
    if (!hit?.entryId) return;
    e.preventDefault();
    e.stopPropagation();
    store.setSelected(hit.entryId, false);
    hideTooltip();
  };

  wrap.addEventListener('pointermove', onMove);
  wrap.addEventListener('pointerleave', onLeave);
  // pointerdown on the wrap fires before any descendant's pointerdown
  // (capture phase) so we can pre-empt text-layer selection when the
  // user clicks ON a redaction. Selection still works everywhere else
  // because hit-test fails outside redaction rects and we return.
  wrap.addEventListener('pointerdown', onClick, true);

  return {
    setHotspots: (next): void => {
      hotspots = next;
      // The entry the user was hovering might have been toggled off
      // by the click — refresh the tooltip state to match.
      const stillVisible = lastHit?.entryId
        ? next.some((h) => h.entryId === lastHit?.entryId)
        : true;
      if (!stillVisible) hideTooltip();
    },
    dispose: (): void => {
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
      wrap.removeEventListener('pointerdown', onClick, true);
      tooltip.remove();
    },
  };
};

